import { ArrowRight, Check } from "lucide-react";
import { PLAN_DEFAULTS } from "@sprout/shared/plans";
import { Overline, Questions, type QA } from "../Layout";
import { usePage } from "../router";
import { SIGN_UP_URL } from "../links";
import {
  FREE,
  LIMITS,
  PAID,
  featureFor,
  limitValue,
  planPrice,
  plusFeatures,
  type LimitId,
} from "../plan-format";

/** The four limits families ask about most, shown as tiles on each plan card. */
const HIGHLIGHTS: [LimitId, string][] = [
  ["wallDevices", "wall screens"],
  ["proofStorageMb", "proof storage"],
  ["proofRetentionMaxDays", "longest proof retention"],
  ["maxVideoSeconds", "longest video"],
];

function Stats({ limits }: { limits: typeof FREE.limits }) {
  return (
    <dl className="pricing-stats">
      {HIGHLIGHTS.map(([id, label]) => (
        <div key={id}>
          <dt>{limitValue(id, limits[id], true)}</dt>
          <dd>{label}</dd>
        </div>
      ))}
    </dl>
  );
}

export const PLAN_QUESTIONS: QA[] = [
  {
    q: "Is AI coaching on automatically?",
    a: `No. It also needs your family’s AI switch, and without it Buddy uses guided coaching. Photos and videos are never sent to AI.`,
  },
  {
    q: "How do I upgrade or cancel?",
    a: `Start on ${FREE.name}, then upgrade from Account → Subscription in the parent app whenever you’re ready. Payment and cancellation run through Stripe’s secure checkout and billing portal. If you cancel, your family keeps every ${FREE.name} tool.`,
  },
  {
    q: "Can I shorten the video limit?",
    a: `Yes. Videos stop at ${PLAN_DEFAULTS.videoMaxSeconds} seconds by default. Choose a shorter limit, down to 10 seconds, under “Longest challenge video” in Account → Privacy. On ${PAID.name} you can raise it to ${PAID.limits.maxVideoSeconds} seconds.`,
  },
];

export default function Pricing() {
  usePage("Pricing · Sprout");
  const free = planPrice(FREE),
    paid = planPrice(PAID);
  const plusOnly = plusFeatures(PAID);
  return (
    <>
      <section className="page-head center site-wrap">
        <Overline center>Pricing</Overline>
        <h1 className="site-h1">Simple plans</h1>
        <p>The core family tools stay free. Plus adds room to grow.</p>
      </section>

      <section className="site-wrap pricing-plans" aria-label="Plans">
        <article className="pricing-plan">
          <span className="pricing-name">{FREE.name}</span>
          <div className="pricing-price">{free.amount}</div>
          <span className="pricing-note">{free.note}</span>
          <p>{FREE.tagline}</p>
          <Stats limits={FREE.limits} />
          <ul className="pricing-features two">
            {FREE.features.map((id) => {
              const f = featureFor(id);
              return f ? (
                <li key={id} title={f.description}>
                  <Check size={17} /> {f.name}
                </li>
              ) : null;
            })}
          </ul>
          <a className="site-btn secondary block" href={SIGN_UP_URL}>
            Create a family
          </a>
        </article>
        <article className="pricing-plan paid">
          <span className="pricing-name">{PAID.name}</span>
          <div className="pricing-price">
            {paid.amount}
            <span>{paid.per}</span>
          </div>
          <span className="pricing-note">{paid.note}</span>
          <p>{PAID.tagline}</p>
          <Stats limits={PAID.limits} />
          <ul className="pricing-features">
            <li>
              <Check size={17} />
              <strong>Everything in {FREE.name}</strong>
            </li>
            {plusOnly.map((f) => (
              <li key={f.id}>
                <Check size={17} />
                <span>
                  <strong>{f.name}</strong>
                  <small>{f.description}</small>
                </span>
              </li>
            ))}
          </ul>
          <a className="site-btn primary block" href={SIGN_UP_URL}>
            Start free, upgrade anytime <ArrowRight size={16} />
          </a>
        </article>
      </section>

      <section className="pricing-compare site-section tinted">
        <div className="site-wrap narrow">
          <h2 className="site-h2 small">Every limit, side by side</h2>
          <div className="pricing-table-scroll">
            <table className="pricing-table">
              <thead>
                <tr>
                  <td />
                  <th scope="col">{FREE.name}</th>
                  <th scope="col">{PAID.name}</th>
                </tr>
              </thead>
              <tbody>
                {LIMITS.map((limit) => {
                  const a = FREE.limits[limit.id],
                    b = PAID.limits[limit.id];
                  return (
                    <tr key={limit.id}>
                      <th scope="row">{limit.name}</th>
                      <td>{limitValue(limit.id, a)}</td>
                      <td className={b > a ? "better" : ""}>{limitValue(limit.id, b)}</td>
                    </tr>
                  );
                })}
                {plusOnly
                  .filter((f) => f.id !== "extended-retention")
                  .map((f) => (
                    <tr key={f.id}>
                      <th scope="row">{f.name}</th>
                      <td className="none">
                        <span aria-hidden="true">—</span>
                        <span className="sr-only">Not included</span>
                      </td>
                      <td className="better">
                        <Check size={18} aria-label="Included" />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="site-section">
        <div className="site-wrap narrow page-qa">
          <h2 className="site-h2 small">Plan questions</h2>
          <Questions items={PLAN_QUESTIONS} openFirst />
        </div>
      </section>
    </>
  );
}
